import Message from '~/components/Message';
import FormGroup from '~/components/form/FormGroup';
import { FormHorizontal } from '~/components/form/layout';
import { useTz } from '~/components/hooks/useTz';
import { InputCardOption } from '~/components/input/InputCards';
import { InputSelectOption } from '~/components/input/InputSelect';
import ZodFieldCheckbox from '~/components/zod/ZodFieldCheckbox';
import ZodFieldDate from '~/components/zod/ZodFieldDate';
import ZodFieldCards from '~/components/zod/ZodFieldCards';
import ZodFieldSelect from '~/components/zod/ZodFieldSelect';
import { useZodFormFieldSingleValue } from '~/components/zod/ZodForm';
import { capitalize } from '~/lib/format';
import { BreOrderContext } from '~/tenants/bre/model/BreOrderContext';
import {
  bre_print_earliest,
  bre_print_speed,
  CONTENT_WRITING_RATE,
  isBusinessDay,
} from '~/tenants/bre/performable/common';
import { layoutSchema } from '~/tenants/bre/performable/print/design/BreDesignConfig';
import { BreDesignRate } from '~/tenants/bre/performable/print/design/BreDesignData';
import {
  FlyerSide,
  countSchema,
  paperSchema,
  sideSchema,
} from '~/tenants/bre/performable/print/flyers/BreFlyersConfig';
import { BreFlyersRate } from '~/tenants/bre/performable/print/flyers/BreFlyersData';
import BreFlyersJob from '~/tenants/bre/performable/print/flyers/BreFlyersJob';
import { TenantPerformableFormProps } from '~/tenants/common/form';
import { TenantPackageAddOnCards, TenantPackageAddOnCheckboxName } from '~/tenants/common/package';
import { TenantPerformableAddOns } from '~/tenants/common/performable';
import { ReactElement } from 'react';
import { BrePrintSpeed } from '~/tenants/bre/data';

const SIDE_OPTS = sideSchema.options.map<InputCardOption>((value) => ({
  title: BreFlyersJob.sidesText(value),
  description: 'flyers',
  value,
}));

const COUNT_OPTS = countSchema.options.map<InputCardOption>((value) => ({
  title: `${value} pieces`,
  description: 'copies',
  value,
}));

export default function BreFlyersForm({
  persona,
  context,
  create,
}: TenantPerformableFormProps<BreOrderContext>): ReactElement {
  const tz = useTz();
  const sides = useZodFormFieldSingleValue<FlyerSide>('sides') ?? '1';
  const needBy = useZodFormFieldSingleValue('need_by', (n) => (n ? tz(n) : tz()));
  const printSpeed = bre_print_speed(context, needBy);
  const earliest = bre_print_earliest(context, 1);

  const paperOptions = paperSchema.options
    .filter((value) => BreFlyersRate[printSpeed][sides]?.[value])
    .map<InputSelectOption>((value) => ({
      label: BreFlyersJob.paperType({ paper: value, sides }),
      value,
    }));

  const designOptions = layoutSchema.options.map<InputCardOption>((value) => {
    const rate = BreDesignRate[value][`flyer_${sides}`];

    return {
      title: `${capitalize(value)} Layout`,
      cost: rate,
      disabled: !rate,
      value,
    };
  });

  return (
    <FormGroup>
      <FormHorizontal name="sides" label="Sides" required>
        <ZodFieldCards name="sides" options={SIDE_OPTS} required />
      </FormHorizontal>
      <FormHorizontal name="count" label="Count" required>
        <ZodFieldCards name="count" options={COUNT_OPTS} cols={2} required />
      </FormHorizontal>
      <FormHorizontal name="paper" label="Paper" required>
        <ZodFieldSelect name="paper" options={paperOptions} />
      </FormHorizontal>
      {sides === '4' && (
        <FormHorizontal name="no_free_uv" label="UV Cover">
          <label className="flex gap-2 cursor-pointer" htmlFor="no_free_uv">
            <ZodFieldCheckbox id="no_free_uv" name="no_free_uv" />
            Opt-out of free UV cover
          </label>
        </FormHorizontal>
      )}
      <FormHorizontal
        name="need_by"
        label="Need By"
        description="We only offer rush printing (for a fee) the day after your scheduled appointment. If you need them sooner, please contact us."
        required
      >
        <ZodFieldDate
          name="need_by"
          filter={(date) => {
            if (!isBusinessDay(date)) {
              return false;
            }

            if (create && date < tz().startOf('day')) {
              // cannot select past
              return false;
            }

            const earliest = bre_print_earliest(context, 1);

            return date.startOf('day') >= earliest.startOf('day');
          }}
        />
      </FormHorizontal>
      {printSpeed === BrePrintSpeed.STANDARD && (
        <Message type="warning">
          <b>Remember:</b> Final approval of your proof is required by{' '}
          <b>{needBy.minus({ day: 1 }).toFormat('M/d')} @ 2 PM</b> in order to receive your printed materials on{' '}
          <b>{needBy.toFormat('M/d')}</b>.
        </Message>
      )}
      {printSpeed === BrePrintSpeed.RUSH && (
        <>
          <Message type="warning">
            <b>Remember:</b> Final approval of your proof is required by <b>{needBy.toFormat('M/d')} @ 10 AM</b>. If
            approval is received after this time, rush printing won&apos;t be possible, and the extra fee won&apos;t
            apply.
          </Message>
          <Message type="info">
            <b>NOTICE:</b> Same-day fee may differ and could impact other charges if adjustments are needed to ensure
            timely completion of your order.
          </Message>
        </>
      )}

      <TenantPerformableAddOns persona={persona}>
        <TenantPackageAddOnCheckboxName
          name="content_writing"
          title="Content writing"
          description="Add content writing for all of your marketing pieces"
          image="tenant/bre/print/content_writing.jpg"
          persona={persona}
          cost={CONTENT_WRITING_RATE}
        />
        <TenantPackageAddOnCards
          name="design"
          title="Graphic Design only"
          description="Include layout design to your flyers"
          image="tenant/bre/print/design.jpg"
          persona={persona}
          options={designOptions}
          cols={2}
          required
        />
      </TenantPerformableAddOns>
    </FormGroup>
  );
}
